import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsGantt from 'highcharts/modules/gantt';
import HighchartsReact from 'highcharts-react-official';
import { Col, Row } from 'react-bootstrap';
// import ErrorBoundary from './ErrorBoundary';
import Legend from './Legend';
import './gantt.css';

HighchartsGantt(Highcharts);

export const Gantt = ({ title, data }) => {
    const chartRef = useRef(null);
    
    // Calculate the full date range from the data
    const getDateRange = () => {
        let minDate = new Date().getTime();
        let maxDate = 0;
        
        data.forEach(item => {
            minDate = Math.min(minDate, item.start);
            maxDate = Math.max(maxDate, item.end);
        });
        
        return { minDate, maxDate };
    };

    const { minDate, maxDate } = getDateRange();

    const handleYTDClick = () => {
        const chart = chartRef.current?.chart;
        if (chart) {
            const now = new Date();
            const startOfYear = Date.UTC(now.getFullYear(), 0, 1);
            chart.xAxis[0].setExtremes(startOfYear, now.getTime());
        }
    };

    const handleYearClick = () => {
        const chart = chartRef.current?.chart;
        if (chart) {
            const now = new Date();
            const oneYearAgo = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
            chart.xAxis[0].setExtremes(oneYearAgo.getTime(), now.getTime());
        }
    };

    const handleAllClick = () => {
        const chart = chartRef.current?.chart;
        if (chart) {
            chart.xAxis[0].setExtremes(null, null);
        }
    };

    // Keep all options in state
    const [chartOptions, setChartOptions] = useState({
        title: {
            text: title
        },
        scrollbar: {
            enabled: true
        },
        rangeSelector: {
            enabled: true,
            selected: 2,
            buttons: [{
                type: 'ytd',
                text: 'YTD',
                events: {
                    click: handleYTDClick
                }
            }, {
                type: 'year',
                count: 1,
                text: 'Year',
                events: {
                    click: handleYearClick
                }
            }, {
                type: 'all',
                text: 'All',
                events: {
                    click: handleAllClick
                }
            }]
        },
        accessibility: {
            enabled: true,
            point: {
                descriptionFormat: '{yCategory}. ' +
                    '{#if completed}Task {(multiply completed.amount 100):.1f}% completed. {/if}' +
                    'Start {x:%Y-%m-%d}, end {x2:%Y-%m-%d}.'
            },
            series: {
                descriptionFormat: '{name}'
            },
            description: 'Gantt chart describing the timeline for key tasks necessary to complete permitting for a Pennsylvania Fast Track project.'
        },
        lang: {
            accessibility: {
                axis: {
                    xAxisDescriptionPlural: 'The chart has a two-part X-axis showing time in years, weeks and days.',
                    yAxisDescriptionPlural: 'The chart has one Y-axis showing tasks and parent tasks.'
                }
            }
        },
        series: [{
            data: data,
            grouping: false
        }],
        xAxis: [{
            tickInterval: 1000 * 60 * 60 * 24 * 30, // Month
            grid: {
                enabled: true,
                borderWidth: 1,
                borderColor: '#E6E6E6'
            },
            currentDateIndicator: {
                width: 2,
                label: {
                    format: '%d %b, %Y',
                    x: -70
                }
            },
            scrollbar: {
                liveRedraw: true
            },
            dateTimeLabelFormats: {
                month: '%m'
            },
            labels: {
                format: '{value:%b}',
                style: {
                    fontSize: '12px'
                }
            }
        },
        {
            tickInterval: 1000 * 60 * 60 * 24 * 365, // Year
            grid: {
                enabled: true,
                borderWidth: 1,
                borderColor: '#E6E6E6'
            },
            labels: {
                format: '{value:%Y}',
                style: {
                    fontSize: '12px'
                }
            },
            linkedTo: 0
        }],
        yAxis: {
            gridLineWidth: 0,
            grid: {
                enabled: true,
                borderWidth: 0,
                columns: [{
                    title: {
                        text: 'Permit',
                        style: {
                            fontSize: '12px',
                            color: '#666666'
                        }
                    }
                }, {
                    title: {
                        text: 'Agency',
                        style: {
                            fontSize: '12px',
                            color: '#666666'
                        }
                    },
                    labels: {
                        format: '{point.agency}'
                    }
                },
                {
                    title: {
                        text: 'Status',
                        style: {
                            fontSize: '12px',
                            color: '#666666'
                        }
                    },
                    labels: {
                        format: '{point.status}'
                    }
                }]
            },
            uniqueNames: true,
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        chart: {
            height: null,
            animation: {
                duration: 300
            },
            events: {
                render: function() {
                    const chart = this;
                    const expandedPoints = chart.series[0].points.filter(p => p.expanded);
                    if (expandedPoints.length > 0) {
                        const visiblePoints = chart.series[0].points.length + 
                            expandedPoints.reduce((acc, point) => {
                                return acc + (point.children?.length || 0);
                            }, 0);
                        const rowHeight = 50;
                        const headerHeight = 100;
                        const newHeight = (visiblePoints * rowHeight) + headerHeight;
                        
                        if (chart.chartHeight !== newHeight) {
                            chart.setSize(undefined, newHeight, true);
                        }
                    }
                }
            }
        },
        plotOptions: {
            gantt: {
                grouping: false,
                pointPadding: 0.2,
                borderRadius: 3,
                dataLabels: {
                    enabled: true
                },
                // rowHeight: 800
            }
        }
    });

    // Update the series in chartOptions when data changes
    useEffect(() => {
        setChartOptions(prevOptions => ({
            ...prevOptions,
            series: [{
                ...prevOptions.series[0],
                data: data
            }]
        }));
    }, [data]);

    return (
        <>
            <Row className="align-items-center mb-3">
                <Col className="legend-container" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Legend />
                </Col>
            </Row>

            <div className="gantt-scroll-wrapper">
                <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={chartOptions}
                    constructorType={"ganttChart"}
                    allowChartUpdate={true}
                    immutable={false}
                    updateArgs={[true, true, true]}
                    containerProps={{ className: 'chartContainer' }}
                />
            </div>
        </>
    );
};
